import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { Loader2, AlertTriangle } from 'lucide-react';
import { apiService, type PromptGuidelineManifestEntry, type SystemGuidelinesType } from '../../../services/api';

interface GuidelinePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  agentType: SystemGuidelinesType;
  content: string;
  manifest: PromptGuidelineManifestEntry;
}

/** Standard-`vars`: erste Ausprägung jeder Varianten-Gruppe (wie das Backend). */
function defaultVars(manifest: PromptGuidelineManifestEntry): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const group of manifest.variantGroups) {
    const first = group.values[0];
    if (first) vars[group.key] = first.value;
  }
  return vars;
}

/**
 * Rendert die Prompt-Vorschau über `POST /preview`. Für Typen mit
 * Varianten-Gruppen steuern Selects (lesbare Labels) die `vars` und laden die
 * Vorschau neu.
 */
export function GuidelinePreviewDialog({ open, onClose, agentType, content, manifest }: GuidelinePreviewDialogProps) {
  const [vars, setVars] = useState<Record<string, string>>(() => defaultVars(manifest));
  const [rendered, setRendered] = useState('');
  const [warnings, setWarnings] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPreview = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await apiService.previewSystemGuideline(agentType, content, vars);
      setRendered(result.rendered);
      setWarnings(result.warnings);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Vorschau konnte nicht geladen werden';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [agentType, content, vars]);

  // Beim Öffnen die Varianten-Auswahl zurücksetzen.
  useEffect(() => {
    if (open) setVars(defaultVars(manifest));
  }, [open, manifest]);

  useEffect(() => {
    if (open) loadPreview();
  }, [open, loadPreview]);

  return (
    <Dialog open={open} onOpenChange={openState => { if (!openState) onClose(); }}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Prompt-Vorschau</DialogTitle>
          <DialogDescription>
            Gerendert mit Beispieldaten und aufgelösten Baustein-Richtlinien. Varianten wählbar.
          </DialogDescription>
        </DialogHeader>

        {manifest.variantGroups.length > 0 && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {manifest.variantGroups.map(group => (
              <div key={group.key} className="space-y-1">
                <Label className="text-xs text-gray-600">{group.label}</Label>
                <Select
                  value={vars[group.key] ?? group.values[0]?.value ?? ''}
                  onValueChange={selected => setVars(prev => ({ ...prev, [group.key]: selected }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {group.values.map(v => (
                      <SelectItem key={v.value} value={v.value}>
                        {v.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        )}

        {error ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Fehler</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : (
          <>
            {warnings.length > 0 && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Hinweise</AlertTitle>
                <AlertDescription>
                  <ul className="list-inside list-disc text-sm">
                    {warnings.map((w, idx) => (
                      <li key={idx}>{w}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
            <div className="relative">
              {isLoading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                </div>
              )}
              <pre className="max-h-[50vh] overflow-auto whitespace-pre-wrap rounded-lg bg-gray-900 p-4 font-mono text-xs text-gray-100">
                {rendered || (isLoading ? '' : '(keine Ausgabe)')}
              </pre>
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={loadPreview} disabled={isLoading}>
            Aktualisieren
          </Button>
          <Button onClick={onClose}>Schließen</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
